import React, { useState, useEffect } from "react";
import { Query, Column, MTableAction, Action } from "material-table";
import { Paper, Box } from "@material-ui/core";
import { AssignmentOutlined as AssignmentIcon, AssessmentOutlined as AssessmentIcon } from "@material-ui/icons";
import { useSnackbar } from "notistack";
import { DefaultTable, ClubSelect, HelpscoutFab } from "components";
import moment from "moment";
import { Club } from "api/clubs";
import { useMounted } from "utils/hooks";
import { getBookings, postBookingExport } from "api/admin/bookings";
import { Booking } from "api/bookings";
import { UserProfile } from "api/user";
import { Membership } from "api/memberships";
import i18n from "./i18n";
import FilterDialog from "./FilterDialog";
import SIPFormDialog from "./SIPFormDialog";
import SIPAggregateDialog from "./SIPAggregateDialog";
import BookingDetailDialog from "./BookingDetailDialog";
import CreateStoryDialog from "./CreateStoryDialog";

// prop types
interface BookingsViewProps {
    club: Club | null,
    memberships: Membership[],
    userProfile: UserProfile | null,
}

// admin bookings table
const BookingsView = ({ club, memberships, userProfile }: BookingsViewProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [clubId, setClubId] = useState<string | undefined>(club?.id);
    const [filters, setFilters] = useState<Parameters<typeof getBookings>[0]>({});
    const [isLoading, setLoading] = useState(false);
    const [showBookingDetailDialog, setShowBookingDetailDialog] = useState<string | null>(null);
    const [showCreateStoryDialog, setShowCreateStoryDialog] = useState<Booking[] | null>(null);
    const [showFilterDialog, setShowFilterDialog] = useState(false);
    const [showSIPAggregateDialog, setShowSIPAggregateDialog] = useState(false);
    const [showSIPFormDialog, setShowSIPFormDialog] = useState<string | null>(null);

    // reset ui
    useEffect(() => setClubId(club?.id), [club]);

    // convert material-table query to API params
    const convertQueryToParams = (query: Query<Booking>) => {
        // paging
        const params: Parameters<typeof getBookings>[0] = {
            limit: query.pageSize,
            offset: query.pageSize * query.page,
        };

        // sorting
        if (query.orderBy) {
            params.sortOrder = query.orderDirection.toUpperCase() as typeof params["sortOrder"];
            params.sortKey = query.orderBy.field as typeof params["sortKey"];
        }

        // filter
        params.clubId = clubId;
        return { ...params, ...filters };
    };

    // export action
    const exportBookings = async () => {
        setLoading(true);
        try {
            const { url } = await postBookingExport({ clubId, ...filters });
            window.open(url);
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // show sip filter
    const permittedMemberships = memberships.filter((m) => ["admin", "owner", "trainer"].includes(m.userRole));
    const showSIPAggregateAction = !!permittedMemberships.find((m) => m.club.capabilities.includes("sip")); // user is member of a sip club

    // table actions
    const isOwner = memberships.find((m) => m.club.id === clubId)?.userRole === "owner";
    const tableActions: Action<Booking>[] = [
        {
            disabled: !isOwner,
            icon: "save_alt",
            isFreeAction: true,
            tooltip: isOwner ? i18n.export : i18n.exportOwnerOnly,
            onClick: exportBookings,
        },
        {
            icon: "filter_list",
            isFreeAction: true,
            tooltip: i18n.filter,
            onClick: () => setShowFilterDialog(true),
        },
        {
            hidden: !showSIPAggregateAction,
            icon: () => <AssessmentIcon />,
            isFreeAction: true,
            tooltip: i18n.sipAggregate,
            onClick: () => setShowSIPAggregateDialog(true),
        },
        {
            icon: "post_add",
            tooltip: i18n.createStory,
            onClick: (e, bookings) => setShowCreateStoryDialog(bookings as Booking[]),
        },
    ];

    // per row actions
    const perRowActions = [
        ({ id }: Booking) => ({
            icon: "edit",
            tooltip: i18n.details,
            onClick: () => setShowBookingDetailDialog(id),
        }),
        ({ id, attachedObjects }: Booking) => ({
            hidden: !attachedObjects.includes("sip-feedback-form"),
            icon: () => <AssignmentIcon />,
            tooltip: i18n.sipForm,
            onClick: () => setShowSIPFormDialog(id),
        }),
    ];

    // table columns
    const tableColumns: Column<Booking>[] = [
        {
            field: "bookingType",
            lookup: i18n.bookingTypes,
            sorting: false,
            title: i18n.bookingType,
        },
        {
            field: "bookingStatus",
            lookup: i18n.bookingStatus,
            sorting: false,
            title: i18n.status,
        },
        {
            field: "clubName",
            lookup: i18n.bookingStatus,
            render: ({ court }) => court.clubName,
            title: i18n.club,
        },
        {
            field: "court.courtName",
            sorting: false,
            title: i18n.court,
        },
        {
            field: "startDate",
            render: ({ startDate }) => moment(startDate).format("DD.MM.YYYY HH:mm"),
            title: i18n.startDate,
        },
        {
            field: "endDate",
            render: ({ endDate }) => moment(endDate).format("DD.MM.YYYY HH:mm"),
            sorting: false,
            title: i18n.endDate,
        },
        {
            render: ({ numAcceptedStandardParticipants }) => numAcceptedStandardParticipants,
            sorting: false,
            title: i18n.participants,
        },
        {
            align: "center",
            cellStyle: { padding: 0 },
            render: (data) => (
                <Box style={{ display: "inline-flex" }}>
                    {perRowActions.map((action, i) => (
                        <MTableAction action={action} data={data} key={i} /> // eslint-disable-line
                    ))}
                </Box>
            ),
            sorting: false,
            title: i18n.actions,
        },
    ];

    // refresh (-> remote data)
    const tableRef = React.useRef<any>(null);
    const isMounted = useMounted();
    const refreshTable = () => {
        if (!isMounted) return; // skip on initial material-table fetch
        if (tableRef.current) tableRef.current.onQueryChange();
    };
    useEffect(refreshTable, [clubId]);

    // render
    return (
        <>
            <HelpscoutFab club={club} />

            {/* club select */}
            <Paper style={{ display: "flex", justifyContent: "flex-end" }}>
                <ClubSelect
                    clubId={clubId}
                    controlProps={{ style: { minWidth: "96px", marginRight: "8px" } }}
                    userRoles={["owner", "admin", "trainer"]}
                    onChange={setClubId}
                />
            </Paper>

            {/* bookings table */}
            <DefaultTable
                actions={tableActions}
                columns={tableColumns}
                data={(query) => new Promise((resolve) => {
                    const params = convertQueryToParams(query);
                    getBookings(params)
                        .then((response) => resolve({
                            ...response,
                            page: query.page,
                        }))
                        .catch((err) => {
                            displayError(err);
                            resolve({ data: [], page: 0, totalCount: 0 });
                        });
                })}
                isLoading={isLoading}
                options={{ search: false, selection: true }}
                tableRef={tableRef}
                title={i18n.bookings}
            />

            {/* dialogs */}
            {showFilterDialog && (
                <FilterDialog
                    clubId={clubId}
                    filters={filters}
                    memberships={memberships}
                    show={showFilterDialog}
                    onClose={() => setShowFilterDialog(false)}
                    onRefreshBookingsTable={refreshTable}
                    onSetClubId={setClubId}
                    onSetFilters={setFilters}
                />
            )}
            {showSIPFormDialog && (
                <SIPFormDialog
                    booking={tableRef.current?.dataManager.data.find((b: Booking) => b.id === showSIPFormDialog)}
                    show={!!showSIPFormDialog}
                    onClose={() => setShowSIPFormDialog(null)}
                />
            )}
            {showSIPAggregateDialog && (
                <SIPAggregateDialog
                    show={showSIPAggregateDialog}
                    onClose={() => setShowSIPAggregateDialog(false)}
                />
            )}
            {showBookingDetailDialog && (
                <BookingDetailDialog
                    bookingId={showBookingDetailDialog}
                    show={!!showBookingDetailDialog}
                    onClose={() => setShowBookingDetailDialog(null)}
                    onRefreshBookingsTable={refreshTable}
                />
            )}
            {showCreateStoryDialog && (
                <CreateStoryDialog
                    bookings={showCreateStoryDialog || undefined}
                    show={!!showCreateStoryDialog}
                    userProfile={userProfile}
                    onClose={() => setShowCreateStoryDialog(null)}
                />
            )}
        </>
    );
};

export default BookingsView;
